import React from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import {
  ModuleRegistry,
  AllCommunityModule,
  themeQuartz,
} from "ag-grid-community";
import styled from "styled-components";
import { IconButton } from "@flexport/latitude-typescript";
import { QuarantineStatus } from "../../../generated/graphql";
import { downloadFile } from "../../../util/downloadUtils";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);
const gridTheme = themeQuartz;

/**
 * AdminFileGrid - AG Grid implementation for File Management tab
 * Displays files with download links, security status, and delete actions
 */

// Styled components for cell renderers
const FileLink = styled.a`
  color: #007bff;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const StatusBadge = styled.span<{ status: QuarantineStatus }>`
  font-weight: bold;
  text-transform: uppercase;
  color: ${(props) => {
    switch (props.status) {
      case QuarantineStatus.Clean:
        return "#28a745";
      case QuarantineStatus.Quarantined:
        return "#dc3545";
      case QuarantineStatus.ScanFailed:
        return "#fd7e14";
      default:
        return "#6c757d";
    }
  }};
  white-space: nowrap;
`;

const SecurityStatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`;

const ScanStatusInfo = styled.div`
  font-size: 10px;
  color: #666;
`;

/**
 * Tooltip wrapper that properly shows tooltip when hovering over button and its children
 * The title attribute on the wrapper will trigger tooltips for the entire area
 */
const TooltipWrapper = styled.div`
  display: inline-flex;
  position: relative;
  cursor: pointer;

  /* Ensure button inherits the cursor and doesn't block tooltip */
  button {
    cursor: pointer;
  }
`;

/**
 * Actions container for button centering
 */
const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

// Row data interface
export interface FileRow {
  id: string;
  fileName: string;
  extension: string;
  createdAt: string;
  fileUrl: string | null;
  folderPath?: string;
  subfolderPath?: string;
  clientId: string;
  quarantineStatus: QuarantineStatus;
  quarantineReason?: string | null;
  scanEngine?: string | null;
  scanEngineVersion?: string | null;
  scanStartedAt?: string | null;
  scanCompletedAt?: string | null;
  client: {
    clientId: string;
    client_name: string;
    folder: string;
  };
  user?: {
    id: string;
    name: string;
    role: string;
  };
}

interface AdminFileGridProps {
  rows: FileRow[];
  onDelete: (id: string) => void;
}

// Helper function to get scan status text
const getScanStatusText = (file: FileRow): string => {
  if (!file.scanEngine) {
    return "Scan pending...";
  }

  if (file.scanStartedAt && !file.scanCompletedAt) {
    return "Scanning...";
  }

  if (file.scanCompletedAt) {
    return `Scanned with ${file.scanEngine}`;
  }

  return "Scan status unknown";
};

// Cell renderer for File Name column with download link
const FileNameCellRenderer: React.FC<ICellRendererParams<FileRow>> = (
  props,
) => {
  const { data } = props;
  if (!data) return null;

  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault();
    downloadFile({
      fileName: data.fileName,
      fileUrl: data.fileUrl || "",
      fileId: data.id,
      clientId: data.clientId,
    });
  };

  return (
    <FileLink href="#" onClick={handleDownload}>
      {data.fileName}
    </FileLink>
  );
};

// Cell renderer for Security Status column
const SecurityStatusCellRenderer: React.FC<ICellRendererParams<FileRow>> = (
  props,
) => {
  const { data } = props;
  if (!data) return null;

  return (
    <SecurityStatusContainer>
      <StatusBadge status={data.quarantineStatus}>
        {data.quarantineStatus}
      </StatusBadge>
      <ScanStatusInfo>{getScanStatusText(data)}</ScanStatusInfo>
      {data.quarantineReason && (
        <ScanStatusInfo>Reason: {data.quarantineReason}</ScanStatusInfo>
      )}
    </SecurityStatusContainer>
  );
};

// Cell renderer for Actions column
const ActionsCellRenderer: React.FC<
  ICellRendererParams<FileRow> & { onDelete: (id: string) => void }
> = (props) => {
  const { data, onDelete } = props;
  if (!data) return null;

  return (
    <ActionsContainer>
      <TooltipWrapper title="Delete file">
        <IconButton
          iconName="trash"
          label="Delete"
          labelAriaOnly={true}
          iconAlignment="left"
          intent="basic"
          kind="hollow"
          size="s"
          type="button"
          isLoading={false}
          width="responsive"
          height={{ type: "fixed" }}
          disabled={false}
          temporaryUnsafeSetSolidKindHack={false}
          onClick={() => onDelete(data.id)}
          testId={`delete-file-${data.id}`}
        />
      </TooltipWrapper>
    </ActionsContainer>
  );
};

// Value getter for Location column
const locationGetter = (params: { data?: FileRow }): string => {
  if (!params.data) return "";
  const { folderPath, subfolderPath, client } = params.data;
  const folder = folderPath || client.folder;
  return subfolderPath ? `${folder}/${subfolderPath}` : folder;
};

// Value getter for Uploaded By column
const uploadedByGetter = (params: { data?: FileRow }): string => {
  if (!params.data) return "";
  const { user } = params.data;
  return user ? `${user.name} (${user.role})` : "Unknown";
};

// Value formatter for Upload Date column
const dateFormatter = (params: { value?: string }): string => {
  if (!params.value) return "";
  const date = new Date(params.value);
  return date.toLocaleString();
};

const AdminFileGrid: React.FC<AdminFileGridProps> = ({ rows, onDelete }) => {
  // Column definitions
  const columnDefs: ColDef<FileRow>[] = [
    {
      headerName: "File Name",
      field: "fileName",
      flex: 2,
      minWidth: 200,
      cellRenderer: FileNameCellRenderer,
      suppressHeaderMenuButton: true,
    },
    {
      headerName: "Type",
      field: "extension",
      flex: 0.8,
      minWidth: 80,
      suppressHeaderMenuButton: true,
    },
    {
      headerName: "Security Status",
      field: "quarantineStatus",
      flex: 1,
      minWidth: 120,
      cellRenderer: SecurityStatusCellRenderer,
      suppressHeaderMenuButton: true,
    },
    {
      headerName: "Location",
      valueGetter: locationGetter,
      flex: 1.5,
      minWidth: 180,
      suppressHeaderMenuButton: true,
    },
    {
      headerName: "Upload Date",
      field: "createdAt",
      flex: 1.5,
      minWidth: 180,
      valueFormatter: dateFormatter,
      suppressHeaderMenuButton: true,
    },
    {
      headerName: "Uploaded By",
      valueGetter: uploadedByGetter,
      flex: 1.5,
      minWidth: 170,
      suppressHeaderMenuButton: true,
    },
    {
      headerName: "Actions",
      sortable: false,
      filter: false,
      width: 100,
      cellRenderer: ActionsCellRenderer,
      cellRendererParams: {
        onDelete,
      },
      suppressHeaderMenuButton: true,
    },
  ];

  // Default column definitions
  const defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    suppressHeaderMenuButton: true,
  };

  return (
    <div style={{ height: "600px", width: "100%" }}>
      <AgGridReact<FileRow>
        theme={gridTheme}
        rowData={rows}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        suppressCellFocus={true}
        animateRows={true}
        pagination={true}
        paginationPageSize={20}
        paginationPageSizeSelector={[20, 50, 100, 999]}
      />
    </div>
  );
};

export default AdminFileGrid;
